/**
 * AiAssetsSection - AI image generation experiments per screen slot
 *
 * Each slot (hero, featured, carousel_0, etc.) has experiments stored as
 * Record<experimentId, AiAssetExperiment> for safe async Firestore updates.
 * Same pattern as strategy.variables and screenBindings.
 *
 * Fire-and-forget generation: frontend sets status='generating',
 * backend writes result directly to screen document via dot-notation.
 *
 * Used in AI editor mode only (mode='ai').
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  SAccordion,
  MediaUploadField,
  DEFAULT_IMAGE_MODEL,
  getProviderForImageModel,
} from '@modules/ui-components';
import { AvailableImageModelSelector, AvailableModelSelector } from '@modules/ai-settings';
import {
  Box, Typography, TextField, Button, Card, CardMedia, Stack, Chip,
  Select, MenuItem, FormControl, InputLabel, IconButton, CircularProgress, Tooltip, Alert, Divider,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { AiAssetSlot, AiAssetExperiment } from '@segmently/schema-models/simplified';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import {
  ASPECT_SIZE_PRESETS,
  DEFAULT_PRESET_ID,
  detectPreset,
  getAspectSizePresetById,
  injectScreenContextIntoPrompt,
  SCREEN_CONTEXT_PROMPT_START,
  type AiAssetScreenContext,
  type MeasureAiAssetSlotContext,
} from '@modules/reactFlow/utils/aiAssetScreenContext';

// ─── Constants ──────────────────────────────────────────────────

// OpenAI quality tiers — labels include verified per-image cost (gpt-image-2,
// 1024×1024) so users see the trade-off before clicking Generate.
const OPENAI_QUALITY_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'low', label: 'Low ($0.006)' },
  { value: 'medium', label: 'Medium ($0.053)' },
  { value: 'high', label: 'High ($0.211)' },
] as const;

function generateExpId(): string {
  return `exp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const DEFAULT_EXPERIMENT: AiAssetExperiment = {
  promptTemplate: '',
  aspectRatio: '1:1',
  imageSize: '1K',
  model: DEFAULT_IMAGE_MODEL,
  provider: getProviderForImageModel(DEFAULT_IMAGE_MODEL),
  quality: 'medium',
  status: 'draft',
};

const DEFAULT_PROMPT_REGENERATION_MODEL = 'gemini-3-flash-preview';
const DEFAULT_PROMPT_REGENERATION_CONCEPT_COUNT = 3;
const MIN_PROMPT_REGENERATION_CONCEPT_COUNT = 1;
const MAX_PROMPT_REGENERATION_CONCEPT_COUNT = 5;

const ASSET_INTENT_ROLE_PREFIX: Record<string, string> = {
  option: 'option',
  carousel_slide: 'carousel',
  progress_step: 'stepper',
  paywall_feature: 'paywall_feature',
  flexible_section: 'section',
};

function formatGeneratedAt(value: unknown): string | null {
  if (!value) return null;
  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'object') {
    const record = value as Record<string, any>;
    if (typeof record.toDate === 'function') {
      date = record.toDate();
    } else if (typeof record.seconds === 'number') {
      date = new Date(record.seconds * 1000 + Math.floor((record.nanoseconds ?? 0) / 1_000_000));
    } else if (typeof record._seconds === 'number') {
      date = new Date(record._seconds * 1000 + Math.floor((record._nanoseconds ?? 0) / 1_000_000));
    }
  }
  return date && Number.isFinite(date.getTime()) ? date.toLocaleString() : null;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readConceptCount(value: unknown): number | null {
  const raw = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim()
      ? Number(value)
      : NaN;
  if (!Number.isInteger(raw)) return null;
  if (raw < MIN_PROMPT_REGENERATION_CONCEPT_COUNT || raw > MAX_PROMPT_REGENERATION_CONCEPT_COUNT) return null;
  return raw;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

function inferImageMimeType(url: string): string {
  const cleanUrl = url.split('?')[0]?.toLowerCase() ?? '';
  if (cleanUrl.endsWith('.jpg') || cleanUrl.endsWith('.jpeg')) return 'image/jpeg';
  if (cleanUrl.endsWith('.webp')) return 'image/webp';
  if (cleanUrl.endsWith('.gif')) return 'image/gif';
  return 'image/png';
}

function mergeReferenceImages(references: Array<ReferenceImageConfig | null | undefined>): ReferenceImageConfig[] {
  const seen = new Set<string>();
  const result: ReferenceImageConfig[] = [];
  for (const reference of references) {
    const url = readString(reference?.url);
    if (!reference || !url || seen.has(url)) continue;
    seen.add(url);
    result.push(reference);
  }
  return result;
}

function getPromptPackageHealth(exp: AiAssetExperiment | undefined, slot?: AiAssetSlot): {
  status?: string;
  warnings: string[];
  failures: string[];
} {
  const health = readResolvedPromptPackage(exp, slot)?.health
    ?? readPromptPackageSet(slot)?.health;
  if (!health || typeof health !== 'object' || Array.isArray(health)) {
    return { warnings: [], failures: [] };
  }
  const record = health as Record<string, unknown>;
  return {
    status: typeof record.status === 'string' ? record.status : undefined,
    warnings: readStringArray(record.warnings),
    failures: readStringArray(record.failures),
  };
}

function hasRenderContext(exp: AiAssetExperiment | undefined, slot?: AiAssetSlot): boolean {
  const context = readResolvedPromptPackage(exp, slot)?.renderContext
    ?? readPromptPackageSet(slot)?.renderContext;
  return isRecord(context);
}

function inferIndexFromPath(path: unknown): number | null {
  if (typeof path !== 'string') return null;
  const matches = [...path.matchAll(/\[(\d+)\]/g)];
  const last = matches.at(-1)?.[1];
  return last ? Number(last) : null;
}

function slotKeyFromAssetIntent(intent: unknown, index: number): string | null {
  if (!intent || typeof intent !== 'object' || Array.isArray(intent)) return null;
  const record = intent as Record<string, unknown>;
  const role = typeof record.slotRole === 'string' && record.slotRole.trim()
    ? record.slotRole.trim()
    : 'hero';
  if (role === 'hero' || role === 'featured') return role;
  const prefix = ASSET_INTENT_ROLE_PREFIX[role];
  if (!prefix) return null;
  const itemIndex = inferIndexFromPath(record.supportsContentPath) ?? index;
  return `${prefix}_${itemIndex}`;
}

function slotKeysFromAssetIntents(step: FlowStepNode): string[] {
  const intents = (step as any)?.aiMeta?.assetIntents;
  if (!Array.isArray(intents)) return [];
  return intents.map(slotKeyFromAssetIntent).filter(Boolean) as string[];
}

function readPromptPackage(exp: AiAssetExperiment | undefined): Record<string, unknown> | null {
  return readRecord((exp as any)?.promptPackage);
}

function readPromptPackageSet(slot: AiAssetSlot | undefined): Record<string, unknown> | null {
  return readRecord((slot as any)?.promptPackageSet);
}

function readExperimentSourcePackageId(
  exp: AiAssetExperiment | undefined,
  slot: AiAssetSlot | undefined,
): string | null {
  return readString((exp as any)?.sourcePackageId)
    ?? readString((exp as any)?.promptPackageId)
    ?? readString((slot as any)?.promptPackageId);
}

function findPromptPackageInSet(
  slot: AiAssetSlot | undefined,
  packageId: string | null,
): Record<string, unknown> | null {
  const variants = readPromptPackageVariants(slot);
  if (variants.length === 0) return null;
  const match = packageId
    ? variants.find((variant) => readString(readRecord(variant.promptPackage)?.packageId) === packageId)
    : null;
  const fallback = match ?? variants[0];
  return readRecord(fallback?.promptPackage);
}

function readResolvedPromptPackage(
  exp: AiAssetExperiment | undefined,
  slot: AiAssetSlot | undefined,
): Record<string, unknown> | null {
  return readPromptPackage(exp)
    ?? findPromptPackageInSet(slot, readExperimentSourcePackageId(exp, slot));
}

function hasUsablePromptSource(exp: AiAssetExperiment | undefined, slot: AiAssetSlot | undefined): boolean {
  if (exp?.promptTemplate?.trim()) return true;
  const promptPackage = readResolvedPromptPackage(exp, slot);
  return Boolean(
    readString(promptPackage?.promptTemplate)
    ?? readString(promptPackage?.prompt),
  );
}

function readPromptPackageVariants(slot: AiAssetSlot | undefined): Array<Record<string, unknown>> {
  const variants = readPromptPackageSet(slot)?.variants;
  return Array.isArray(variants) ? variants.filter(isRecord) : [];
}

// ─── Props ──────────────────────────────────────────────────────

interface AiAssetsSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  onGenerateImage?: (slotKey: string, config: GenerateImageConfig) => Promise<GenerateImageResult>;
  onApplyGeneratedAsset?: (slotKey: string, experimentId: string) => void;
  onApplyAllGeneratedAssets?: () => void;
  projectId?: string;
  /** Strategy ID — NOT onboardingId. Used for CDN upload path. */
  strategyId?: string;
  /** Strategy block ID — NOT versionId. Used for CDN upload path. */
  blockId?: string;
  /** Measures the real rendered V2ScreenView context for a slot. */
  measureSlotContext?: MeasureAiAssetSlotContext;
  /** Starts an AI backend task that rewrites the selected prompt package/experiment. */
  onRegeneratePrompt?: (slotKey: string, config: RegenerateAssetPromptConfig) => Promise<RegenerateAssetPromptResult>;
}

export interface RegenerateAssetPromptConfig {
  sourceExperimentId?: string;
  experimentId?: string;
  promptAuthoringMode?: 'initial' | 'revision';
  instruction?: string;
  model?: string;
  conceptCount?: number;
  renderContext?: AiAssetScreenContext | null;
  selectRegenerated?: boolean;
}

export interface RegenerateAssetPromptResult {
  success: boolean;
  taskId?: string;
  experimentId?: string;
  error?: string;
}

export interface GenerateImageConfig {
  prompt: string;
  negativePrompt?: string;
  aspectRatio?: string;
  imageSize?: string;
  /** OpenAI-only explicit size override. */
  openaiSize?: string;
  /** OpenAI-only quality tier. */
  quality?: 'low' | 'medium' | 'high' | 'auto';
  style?: string;
  model?: string;
  /** Image provider — 'google' | 'openai'. Derived from model when omitted. */
  provider?: 'google' | 'openai';
  experimentId?: string;
  referenceImages?: Array<{
    url: string;
    mimeType: string;
    role?: string;
    name?: string;
    instruction?: string;
    includeInGeneration?: boolean;
  }>;
}

type ReferenceImageConfig = NonNullable<GenerateImageConfig['referenceImages']>[number];

type TemplateReferenceImage = ReferenceImageConfig & {
  templateId: string;
  templateName: string;
  contentPath: string;
};

export interface GenerateImageResult {
  success: boolean;
  taskId?: string;
  imageUrl?: string;
  generationModel?: string;
  error?: string;
}

// ─── Component ──────────────────────────────────────────────────

export const AiAssetsSection: React.FC<AiAssetsSectionProps> = ({
  step,
  onChange,
  disabled,
  defaultExpanded = true,
  onGenerateImage,
  onApplyGeneratedAsset,
  onApplyAllGeneratedAssets,
  projectId,
  strategyId,
  blockId,
  measureSlotContext,
  onRegeneratePrompt,
}) => {
  const aiAssets: Record<string, AiAssetSlot> = (step as any).aiAssets ?? {};
  const slotKeys = useMemo(() => Array.from(new Set([
    ...Object.keys(aiAssets),
    ...slotKeysFromAssetIntents(step),
  ])), [aiAssets, step]);
  const hasSlots = slotKeys.length > 0;

  const [generatingKeys, setGeneratingKeys] = useState<Set<string>>(new Set());
  const [regeneratingPromptKeys, setRegeneratingPromptKeys] = useState<Set<string>>(new Set());
  const [regeneratePromptErrorBySlotKey, setRegeneratePromptErrorBySlotKey] = useState<Record<string, string>>({});
  const [visibleContextSlotKey, setVisibleContextSlotKey] = useState<string | null>(null);
  const [contextBySlotKey, setContextBySlotKey] = useState<Record<string, AiAssetScreenContext>>({});
  const [promptRegenerationDialog, setPromptRegenerationDialog] = useState<{ slotKey: string; experimentId: string; renderContext: AiAssetScreenContext | null } | null>(null);
  const [promptRegenerationUseContext, setPromptRegenerationUseContext] = useState(false);
  const [promptRegenerationInstruction, setPromptRegenerationInstruction] = useState('');
  const [promptRegenerationModel, setPromptRegenerationModel] = useState(DEFAULT_PROMPT_REGENERATION_MODEL);
  const [promptRegenerationConceptCount, setPromptRegenerationConceptCount] = useState(DEFAULT_PROMPT_REGENERATION_CONCEPT_COUNT);
  const storageService = useMemo(() => new StorageService(), []);

  // ─── Helpers ──────────────────────────────────────────────────

  /** Get experiments as Record (handle both old array and new dict formats) */
  const getExperiments = useCallback((slot: AiAssetSlot): Record<string, AiAssetExperiment> => {
    const raw = slot?.experiments;
    if (!raw) return {};
    // Already a Record
    if (!Array.isArray(raw)) return raw as Record<string, AiAssetExperiment>;
    // Legacy array → convert to Record
    const result: Record<string, AiAssetExperiment> = {};
    (raw as any[]).forEach((exp: AiAssetExperiment) => {
      result[generateExpId()] = exp;
    });
    return result;
  }, []);

  // ─── Experiment CRUD ──────────────────────────────────────────

  const updateExperiment = useCallback((slotKey: string, expId: string, field: string, value: unknown) => {
    onChange(`aiAssets.${slotKey}.experiments.${expId}.${field}`, value);
  }, [onChange]);

  const selectExperiment = useCallback((slotKey: string, expId: string) => {
    onChange(`aiAssets.${slotKey}.selectedExperimentId`, expId);
  }, [onChange]);

  const addExperiment = useCallback((slotKey: string) => {
    const newId = generateExpId();
    onChange(`aiAssets.${slotKey}.experiments.${newId}`, { ...DEFAULT_EXPERIMENT });
    onChange(`aiAssets.${slotKey}.selectedExperimentId`, newId);
  }, [onChange]);

  const getSlotContext = useCallback((slotKey: string): AiAssetScreenContext | null => {
    if (!measureSlotContext) return null;
    const context = measureSlotContext(slotKey);
    setContextBySlotKey(prev => ({ ...prev, [slotKey]: context }));
    return context;
  }, [measureSlotContext]);

  const applyContextPresetToExperiment = useCallback((
    slotKey: string,
    expId: string | undefined,
    context: AiAssetScreenContext | null | undefined,
  ) => {
    if (!expId || !context || (context.source !== 'measured' && context.source !== 'measured-shared')) return;
    const preset = getAspectSizePresetById(context.aspectRatio?.nearestPresetId);
    if (!preset) return;
    updateExperiment(slotKey, expId, 'aspectRatio', preset.aspectRatio);
    updateExperiment(slotKey, expId, 'imageSize', preset.imageSize);
    updateExperiment(slotKey, expId, 'openaiSize' as any, preset.openaiSize);
  }, [updateExperiment]);

  const handleShowSlotContext = useCallback((slotKey: string) => {
    const context = getSlotContext(slotKey);
    if (context) {
      setVisibleContextSlotKey(prev => prev === slotKey ? null : slotKey);
    }
  }, [getSlotContext]);

  const handleInjectSlotContext = useCallback((slotKey: string, expId: string, exp: AiAssetExperiment) => {
    const context = getSlotContext(slotKey) ?? contextBySlotKey[slotKey];
    if (!context) return;

    updateExperiment(
      slotKey,
      expId,
      'promptTemplate',
      injectScreenContextIntoPrompt(exp.promptTemplate, context),
    );

    applyContextPresetToExperiment(slotKey, expId, context);
  }, [applyContextPresetToExperiment, contextBySlotKey, getSlotContext, updateExperiment]);

  const handleOpenPromptRegeneration = useCallback((slotKey: string, experimentId: string, activeExp?: AiAssetExperiment) => {
    const measuredContext = getSlotContext(slotKey) ?? contextBySlotKey[slotKey] ?? null;
    const packageContext = readResolvedPromptPackage(activeExp, aiAssets[slotKey])?.renderContext as AiAssetScreenContext | undefined;
    const renderContext = measuredContext ?? packageContext ?? null;
    if (renderContext) {
      setContextBySlotKey(prev => ({ ...prev, [slotKey]: renderContext }));
    }
    setPromptRegenerationDialog({ slotKey, experimentId, renderContext });
    setPromptRegenerationUseContext(Boolean(renderContext));
    setPromptRegenerationInstruction('');
    const previousModel = readString((activeExp as any)?.promptRegeneration?.model);
    setPromptRegenerationModel(previousModel ?? DEFAULT_PROMPT_REGENERATION_MODEL);
    setPromptRegenerationConceptCount(
      readConceptCount((activeExp as any)?.promptRegeneration?.conceptCount)
        ?? DEFAULT_PROMPT_REGENERATION_CONCEPT_COUNT,
    );
    setRegeneratePromptErrorBySlotKey(prev => ({ ...prev, [slotKey]: '' }));
  }, [aiAssets, contextBySlotKey, getSlotContext]);

  const handleCreatePromptExperiment = useCallback((slotKey: string) => {
    const newId = generateExpId();
    const experiment = { ...DEFAULT_EXPERIMENT };
    onChange(`aiAssets.${slotKey}.experiments.${newId}`, experiment);
    onChange(`aiAssets.${slotKey}.selectedExperimentId`, newId);
    handleOpenPromptRegeneration(slotKey, newId, experiment);
  }, [handleOpenPromptRegeneration, onChange]);

  const handleClosePromptRegeneration = useCallback(() => {
    setPromptRegenerationDialog(null);
    setPromptRegenerationUseContext(false);
    setPromptRegenerationInstruction('');
    setPromptRegenerationConceptCount(DEFAULT_PROMPT_REGENERATION_CONCEPT_COUNT);
  }, []);

  const handleSubmitPromptRegeneration = useCallback(async () => {
    if (!promptRegenerationDialog || !onRegeneratePrompt) return;
    const { slotKey, experimentId } = promptRegenerationDialog;
    const availableRenderContext = promptRegenerationDialog.renderContext
      ?? getSlotContext(slotKey)
      ?? contextBySlotKey[slotKey]
      ?? null;
    const renderContext = promptRegenerationUseContext ? availableRenderContext : null;
    const regenKey = `${slotKey}:${experimentId}`;
    setRegeneratingPromptKeys(prev => new Set(prev).add(regenKey));
    setRegeneratePromptErrorBySlotKey(prev => ({ ...prev, [slotKey]: '' }));

    try {
      const sourceExperiment = getExperiments(aiAssets[slotKey])?.[experimentId];
      const promptAuthoringMode = hasUsablePromptSource(sourceExperiment, aiAssets[slotKey]) ? 'revision' : 'initial';
      const result = await onRegeneratePrompt(slotKey, {
        sourceExperimentId: promptAuthoringMode === 'revision' ? experimentId : undefined,
        promptAuthoringMode,
        instruction: promptRegenerationInstruction.trim() || undefined,
        model: promptRegenerationModel,
        conceptCount: promptRegenerationConceptCount,
        renderContext,
        selectRegenerated: true,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to start prompt regeneration');
      }
      handleClosePromptRegeneration();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start prompt regeneration';
      setRegeneratePromptErrorBySlotKey(prev => ({ ...prev, [slotKey]: message }));
    } finally {
      setRegeneratingPromptKeys(prev => {
        const next = new Set(prev);
        next.delete(regenKey);
        return next;
      });
    }
  }, [
    handleClosePromptRegeneration,
    aiAssets,
    contextBySlotKey,
    getExperiments,
    getSlotContext,
    onRegeneratePrompt,
    promptRegenerationDialog,
    promptRegenerationInstruction,
    promptRegenerationConceptCount,
    promptRegenerationModel,
    promptRegenerationUseContext,
  ]);

  const duplicateExperiment = useCallback((slotKey: string, sourceId: string) => {
    const experiments = getExperiments(aiAssets[slotKey]);
    const source = experiments[sourceId];
    if (!source) return;
    const newId = generateExpId();
    const copy: AiAssetExperiment = {
      ...JSON.parse(JSON.stringify(source)),
      status: 'draft',
      generatedUrl: undefined,
      generationModel: undefined,
      generatedAt: undefined,
      error: undefined,
    };
    onChange(`aiAssets.${slotKey}.experiments.${newId}`, copy);
    onChange(`aiAssets.${slotKey}.selectedExperimentId`, newId);
  }, [aiAssets, getExperiments, onChange]);

  const deleteExperiment = useCallback((slotKey: string, expId: string) => {
    const experiments = getExperiments(aiAssets[slotKey]);
    const ids = Object.keys(experiments);
    if (ids.length <= 1) return; // Keep at least 1

    // Remove by setting to null (Firestore deleteField equivalent via onChange)
    const updated = { ...experiments };
    delete updated[expId];
    onChange(`aiAssets.${slotKey}.experiments`, updated);

    // Update selection
    const remaining = Object.keys(updated);
    const selectedId = aiAssets[slotKey]?.selectedExperimentId;
    if (selectedId === expId) {
      onChange(`aiAssets.${slotKey}.selectedExperimentId`, remaining[0]);
    }
  }, [aiAssets, getExperiments, onChange]);

  const getConsistencyAnchorReferences = useCallback((
    slotKey: string,
    expId: string,
    existingRefs: NonNullable<GenerateImageConfig['referenceImages']>,
  ): NonNullable<GenerateImageConfig['referenceImages']> => {
    const existingUrls = new Set(existingRefs.map((ref) => ref.url).filter(Boolean));
    const anchors: NonNullable<GenerateImageConfig['referenceImages']> = [];

    Object.entries(aiAssets)
      .filter(([candidateSlotKey]) => candidateSlotKey !== slotKey)
      .sort(([aKey, aSlot], [bKey, bSlot]) => {
        const aOrder = typeof (aSlot as any).order === 'number' ? (aSlot as any).order : Number.MAX_SAFE_INTEGER;
        const bOrder = typeof (bSlot as any).order === 'number' ? (bSlot as any).order : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder || aKey.localeCompare(bKey);
      })
      .forEach(([candidateSlotKey, slot]) => {
        if (anchors.length >= 2) return;
        const experiments = getExperiments(slot);
        const selectedId = slot.selectedExperimentId ?? Object.keys(experiments)[0];
        if (!selectedId || selectedId === expId) return;
        const selected = experiments[selectedId];
        const generatedUrl = selected?.generatedUrl;
        if (selected?.status !== 'completed' || !generatedUrl || existingUrls.has(generatedUrl)) return;
        existingUrls.add(generatedUrl);
        anchors.push({
          url: generatedUrl,
          mimeType: 'image/png',
          role: 'anchor',
          name: `${candidateSlotKey} generated style anchor`,
          instruction: 'Use as an onboarding asset style anchor only: preserve palette, lighting or illustration treatment, density, spacing rhythm, and motif without duplicating the exact scene, layout, or generated content.',
        });
      });

    return anchors;
  }, [aiAssets, getExperiments]);

  const completedSelectedSlotCount = useMemo(() => {
    return slotKeys.reduce((count, slotKey) => {
      const slot = aiAssets[slotKey];
      const experiments = getExperiments(slot);
      const selectedId = slot?.selectedExperimentId ?? Object.keys(experiments)[0];
      const exp = selectedId ? experiments[selectedId] : undefined;
      return exp?.status === 'completed' && !!exp.generatedUrl ? count + 1 : count;
    }, 0);
  }, [aiAssets, getExperiments, slotKeys]);
  const promptRegenerationSlot = promptRegenerationDialog
    ? aiAssets[promptRegenerationDialog.slotKey]
    : undefined;
  const promptRegenerationExperiments = promptRegenerationSlot
    ? getExperiments(promptRegenerationSlot)
    : {};
  const promptRegenerationSourceExperiment = promptRegenerationDialog
    ? promptRegenerationExperiments[promptRegenerationDialog.experimentId]
    : undefined;
  const promptRegenerationHasPrompt = hasUsablePromptSource(promptRegenerationSourceExperiment, promptRegenerationSlot);
  const promptRegenerationActionLabel = promptRegenerationHasPrompt ? 'Regenerate prompt' : 'Generate prompt';
  const promptRegenerationKey = promptRegenerationDialog
    ? `${promptRegenerationDialog.slotKey}:${promptRegenerationDialog.experimentId}`
    : '';
  const isPromptRegenerationSubmitting = promptRegenerationKey
    ? regeneratingPromptKeys.has(promptRegenerationKey)
    : false;

  // ─── Generate ─────────────────────────────────────────────────

  const handleGenerate = useCallback(async (slotKey: string, expId: string) => {
    if (!onGenerateImage) return;
    const experiments = getExperiments(aiAssets[slotKey]);
    const exp = experiments[expId];
    if (!exp?.promptTemplate?.trim()) return;

    const genKey = `${slotKey}:${expId}`;
    setGeneratingKeys(prev => new Set(prev).add(genKey));
    updateExperiment(slotKey, expId, 'status', 'generating');

    try {
      // Fire-and-forget: pass experimentId. Backend writes result to Firestore screen doc.
      const model = exp.model || DEFAULT_IMAGE_MODEL;
      const provider = (exp.provider as 'google' | 'openai' | undefined)
        ?? getProviderForImageModel(model);
      const manualReferences = exp.referenceImages ?? [];
      const referenceImages = mergeReferenceImages([
        ...manualReferences,
        ...getConsistencyAnchorReferences(slotKey, expId, manualReferences),
      ]).slice(0, 14);
      const result = await onGenerateImage(slotKey, {
        prompt: exp.promptTemplate,
        negativePrompt: exp.negativePrompt,
        aspectRatio: exp.aspectRatio || '1:1',
        // Gemini reads imageSize directly; OpenAI ignores it (uses openaiSize).
        imageSize: exp.imageSize || '1K',
        openaiSize: (exp as any).openaiSize,
        quality: (exp as any).quality,
        style: exp.style,
        model,
        provider,
        referenceImages,
        experimentId: expId,
      });

      if (!result.success) {
        updateExperiment(slotKey, expId, 'status', 'error');
        updateExperiment(slotKey, expId, 'error', result.error || 'Failed to start generation');
      }
      // If success — status stays 'generating'. Backend updates to 'completed' via Firestore.
    } catch (err) {
      updateExperiment(slotKey, expId, 'status', 'error');
      updateExperiment(slotKey, expId, 'error', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setGeneratingKeys(prev => {
        const next = new Set(prev);
        next.delete(genKey);
        return next;
      });
    }
  }, [aiAssets, getExperiments, getConsistencyAnchorReferences, onGenerateImage, updateExperiment]);

  // ─── Reference image upload ───────────────────────────────────

  const handleRefChange = useCallback(async (slotKey: string, expId: string, refIdx: number, value: string | File | null) => {
    const experiments = getExperiments(aiAssets[slotKey]);
    const exp = experiments[expId];
    if (!exp) return;
    const refs = [...(exp.referenceImages ?? [])];

    if (value === null) {
      refs.splice(refIdx, 1);
    } else if (value instanceof File) {
      const sanitizedName = value.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `strategies/${strategyId || 'unknown'}/blocks/${blockId || 'unknown'}/ref-images/${Date.now()}_${sanitizedName}`;
      const result = await storageService.uploadImage(path, value);
      refs[refIdx] = { url: result.assetUrl.origin, mimeType: value.type || 'image/png', role: refs[refIdx]?.role || 'object' };
    } else {
      refs[refIdx] = { ...refs[refIdx], url: value };
    }
    updateExperiment(slotKey, expId, 'referenceImages', refs);
  }, [aiAssets, getExperiments, strategyId, blockId, storageService, updateExperiment]);

  const addReference = useCallback((slotKey: string, expId: string) => {
    const experiments = getExperiments(aiAssets[slotKey]);
    const exp = experiments[expId];
    const refs = [...(exp?.referenceImages ?? [])];
    refs.push({ url: '', mimeType: 'image/png', role: 'object' as const });
    updateExperiment(slotKey, expId, 'referenceImages', refs);
  }, [aiAssets, getExperiments, updateExperiment]);

  // ─── Render ───────────────────────────────────────────────────

  return (
    <SAccordion
      title="AI Assets"
      defaultExpanded={defaultExpanded}
      collapsible
      subtitle={hasSlots ? `${slotKeys.length} slot(s)` : undefined}
    >
      <Box data-testid="ai-assets-section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {hasSlots && onApplyAllGeneratedAssets && completedSelectedSlotCount > 0 && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={onApplyAllGeneratedAssets}
              disabled={disabled}
            >
              Apply all
            </Button>
          </Stack>
        )}

        {!hasSlots && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No AI asset slots for this screen.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Asset slots are created during AI block generation or from saved asset intents.
            </Typography>
          </Box>
        )}

        {slotKeys.map((slotKey) => {
          const slot = aiAssets[slotKey] ?? ({ experiments: {} } as AiAssetSlot);
          const experiments = getExperiments(slot);
          const experimentEntries = Object.entries(experiments);
          const selectedId = slot.selectedExperimentId ?? experimentEntries[0]?.[0];
          const activeExp = selectedId ? experiments[selectedId] : undefined;
          const isGenerating = selectedId
            ? generatingKeys.has(`${slotKey}:${selectedId}`) || activeExp?.status === 'generating'
            : false;
          const isRegeneratingPrompt = selectedId
            ? regeneratingPromptKeys.has(`${slotKey}:${selectedId}`)
            : false;
          const hasPrompt = !!activeExp?.promptTemplate?.trim();
          const canApplyActive = !!(
            selectedId &&
            activeExp?.status === 'completed' &&
            activeExp.generatedUrl &&
            onApplyGeneratedAsset
          );
          const slotContext = contextBySlotKey[slotKey];
          const isContextVisible = visibleContextSlotKey === slotKey;
          const promptHealth = getPromptPackageHealth(activeExp, slot);
          const promptHealthSeverity = promptHealth.failures.length > 0
            ? 'error'
            : promptHealth.warnings.length > 0 ? 'warning' : null;
          const variantLabel = (activeExp as any)?.promptVariantLabel;
          const promptHypothesis = (activeExp as any)?.promptHypothesis;
          const regeneratePromptError = regeneratePromptErrorBySlotKey[slotKey];
          const promptPackage = readResolvedPromptPackage(activeExp, slot);
          const promptPackageSet = readPromptPackageSet(slot);
          const promptAlreadyHasContext = Boolean(activeExp?.promptTemplate?.includes(SCREEN_CONTEXT_PROMPT_START));
          const localContextActionLabel = promptAlreadyHasContext ? 'Refresh in prompt' : 'Insert into prompt';
          const variants = readPromptPackageVariants(slot);
          const referenceImageCount = activeExp?.referenceImages?.length ?? 0;

          return (
            <Card key={slotKey} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack divider={<Divider flexItem />}>
                {/* Slot Header */}
                <Stack spacing={1.25} sx={{ p: 1.5 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip label={slotKey} size="small" variant="outlined" color="primary" />
                        {activeExp?.status === 'completed' && (
                          <Chip icon={<CheckCircleIcon />} label="Generated" size="small" color="success" variant="outlined" />
                        )}
                        {promptHealth.status && (
                          <Chip
                            label={`Health: ${promptHealth.status}`}
                            size="small"
                            color={promptHealth.failures.length > 0 ? 'error' : promptHealth.warnings.length > 0 ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {readString(promptPackage?.packageId)
                          ? `Package ${readString(promptPackage?.packageId)}`
                          : promptPackageSet
                            ? 'Package set available'
                            : 'Prompt metadata unavailable'}
                        {variants.length > 0 ? ` · ${variants.length} variant(s)` : ''}
                      </Typography>
                    </Stack>
                    {activeExp?.status === 'completed' && onApplyGeneratedAsset && (
                      <Tooltip title={canApplyActive ? 'Apply generated image to preview' : 'Generate an image before applying'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            aria-label={`Apply ${slotKey} generated image`}
                            onClick={() => selectedId && onApplyGeneratedAsset(slotKey, selectedId)}
                            disabled={disabled || !canApplyActive}
                            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                          >
                            Apply
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>

                  {measureSlotContext && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Tooltip title="View real screen container context">
                        <span>
                          <Button
                            size="small"
                            variant={isContextVisible ? 'contained' : 'outlined'}
                            startIcon={<TuneIcon />}
                            onClick={() => handleShowSlotContext(slotKey)}
                            disabled={disabled}
                            data-testid={`ai-asset-context-view-${slotKey}`}
                            sx={{ minWidth: { sm: 116 } }}
                          >
                            Context
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>

                {(experimentEntries.length > 0 || activeExp || onRegeneratePrompt) && (
                  <Stack spacing={1.5} sx={{ p: 1.5 }}>
                    {/* ─── Experiment Tabs ─── */}
                    {experimentEntries.length > 0 && (
                      <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.5 }} alignItems="center">
                        {experimentEntries.map(([expId, exp]) => (
                        <Box
                          key={expId}
                          onClick={() => selectExperiment(slotKey, expId)}
                          sx={{
                            width: 56,
                            height: 56,
                            minWidth: 56,
                            borderRadius: 1,
                            border: 2,
                            overflow: 'hidden',
                            borderColor: expId === selectedId ? 'primary.main' : 'divider',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                            position: 'relative',
                          }}
                        >
                          {exp.status === 'completed' && exp.generatedUrl ? (
                            <Box component="img" src={exp.generatedUrl} alt={expId} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : exp.status === 'generating' ? (
                            <CircularProgress size={20} />
                          ) : exp.status === 'error' ? (
                            <ErrorIcon color="error" fontSize="small" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {experimentEntries.findIndex(([id]) => id === expId) + 1}
                            </Typography>
                          )}
                        </Box>
                        ))}
                        <Tooltip title="Add Experiment">
                          <IconButton size="small" onClick={() => addExperiment(slotKey)} disabled={disabled}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}

                    {/* ─── Active Experiment Form ─── */}
                    {!activeExp && (
                      <Stack spacing={1}>
                        <Alert severity="info" data-testid={`ai-asset-prompt-empty-${slotKey}`}>
                          No prompt experiment exists for this slot yet.
                        </Alert>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AutoAwesomeIcon />}
                          onClick={() => handleCreatePromptExperiment(slotKey)}
                          disabled={disabled || !onRegeneratePrompt}
                          data-testid={`ai-asset-prompt-regenerate-${slotKey}`}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Generate prompt
                        </Button>
                      </Stack>
                    )}

                    {activeExp && selectedId && (
                      <>
                    {(variantLabel || hasRenderContext(activeExp, slot) || promptHealth.status) && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {variantLabel && (
                          <Chip label={`Variant: ${variantLabel}`} size="small" variant="outlined" />
                        )}
                        {hasRenderContext(activeExp, slot) && (
                          <Chip label="Render context enriched" size="small" color="info" variant="outlined" />
                        )}
                        {promptHealth.status && (
                          <Chip
                            label={`Prompt health: ${promptHealth.status}`}
                            size="small"
                            color={promptHealth.failures.length > 0 ? 'error' : promptHealth.warnings.length > 0 ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    )}

                    {promptHypothesis && (
                      <Typography variant="caption" color="text.secondary">
                        {promptHypothesis}
                      </Typography>
                    )}

                    {promptHealthSeverity && (
                      <Alert severity={promptHealthSeverity} sx={{ alignItems: 'flex-start' }}>
                        <Stack spacing={0.5}>
                          {promptHealth.failures.map((failure) => (
                            <Typography key={`failure-${failure}`} variant="caption">{failure}</Typography>
                          ))}
                          {promptHealth.warnings.map((warning) => (
                            <Typography key={`warning-${warning}`} variant="caption">{warning}</Typography>
                          ))}
                        </Stack>
                      </Alert>
                    )}

                    {/* Preview */}
                    {activeExp.generatedUrl && (
                      <>
                        <CardMedia
                          component="img"
                          image={activeExp.generatedUrl}
                          alt={`Generated ${slotKey}`}
                          data-testid={`ai-asset-generated-preview-${slotKey}`}
                          sx={{ borderRadius: 1, maxHeight: 300, objectFit: 'contain', bgcolor: 'action.hover' }}
                        />
                        <TextField
                          value={activeExp.generatedUrl}
                          size="small"
                          fullWidth
                          slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.7rem' } } }}
                          onClick={(e) => {
                            (e.target as HTMLInputElement).select();
                            navigator.clipboard.writeText(activeExp.generatedUrl!);
                          }}
                          helperText="Click to copy URL"
                        />
                      </>
                    )}

                    {/* Prompt */}
                    <TextField
                      label="Image Prompt"
                      value={activeExp.promptTemplate ?? ''}
                      onChange={(e) => updateExperiment(slotKey, selectedId, 'promptTemplate', e.target.value)}
                      multiline minRows={2} maxRows={6} fullWidth size="small"
                      disabled={disabled} placeholder="Describe the image for AI generation..."
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isRegeneratingPrompt ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                        onClick={() => handleOpenPromptRegeneration(slotKey, selectedId, activeExp)}
                        disabled={disabled || isRegeneratingPrompt || !onRegeneratePrompt}
                        data-testid={`ai-asset-prompt-regenerate-${slotKey}`}
                      >
                        {isRegeneratingPrompt ? 'Starting prompt task...' : hasPrompt ? 'Regenerate prompt' : 'Generate prompt'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Starts an AI task that creates a new prompt experiment.
                      </Typography>
                    </Stack>

                    {regeneratePromptError && (
                      <Alert severity="error" data-testid={`ai-asset-prompt-regenerate-error-${slotKey}`}>
                        {regeneratePromptError}
                      </Alert>
                    )}

                    {measureSlotContext && isContextVisible && slotContext && (
                      <Alert
                        severity={slotContext.source === 'unavailable' || slotContext.source === 'schema-fallback' ? 'warning' : 'info'}
                        data-testid={`ai-asset-context-panel-${slotKey}`}
                        sx={{ alignItems: 'flex-start' }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => handleInjectSlotContext(slotKey, selectedId, activeExp)}
                            disabled={disabled || slotContext.source === 'unavailable'}
                            data-testid={`ai-asset-context-inject-${slotKey}`}
                          >
                            {localContextActionLabel}
                          </Button>
                        }
                      >
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              Screen container context
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={promptAlreadyHasContext ? 'success' : 'default'}
                              label={promptAlreadyHasContext ? 'Inserted' : 'Not inserted'}
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' } }}
                            />
                          </Stack>
                          <Typography variant="caption">
                            Source: {slotContext.source} ({slotContext.confidence})
                            {slotContext.device ? ` · ${slotContext.device}` : ''}
                          </Typography>
                          {slotContext.slotRect && (
                            <Typography variant="caption">
                              Slot: {slotContext.slotRect.width} x {slotContext.slotRect.height}px
                              {slotContext.aspectRatio ? ` · ${slotContext.aspectRatio.label}` : ''}
                            </Typography>
                          )}
                          {!slotContext.slotRect && slotContext.screenRect && (
                            <Typography variant="caption">
                              Screen: {slotContext.screenRect.width} x {slotContext.screenRect.height}px
                              {` · ${slotContext.screenRect.aspect}`}
                            </Typography>
                          )}
                          {slotContext.aspectRatio?.nearestPresetLabel && (
                            <Typography variant="caption">
                              Preset: {slotContext.aspectRatio.nearestPresetLabel}
                            </Typography>
                          )}
                          {slotContext.position && (
                            <Typography variant="caption">
                              Position: {slotContext.position.verticalBand}/{slotContext.position.horizontalBand}
                              {` · ${slotContext.position.widthPercentOfScreen}%w x ${slotContext.position.heightPercentOfScreen}%h`}
                            </Typography>
                          )}
                          {slotContext.background && (
                            <Typography variant="caption">
                              Background: {slotContext.background.screenKind}
                              {slotContext.background.screenColor ? ` ${slotContext.background.screenColor}` : ''}
                              {slotContext.background.luminance ? ` (${slotContext.background.luminance})` : ''}
                            </Typography>
                          )}
                          {!!slotContext.warnings?.length && (
                            <Typography variant="caption" color="text.secondary">
                              {slotContext.warnings.join(' ')}
                            </Typography>
                          )}
                        </Stack>
                      </Alert>
                    )}

                    {/* Model — first control. Switching the model re-derives
                        the provider, which in turn changes which secondary
                        controls are visible (Quality is OpenAI-only). */}
                    <AvailableImageModelSelector
                      projectId={projectId}
                      value={activeExp.model || DEFAULT_IMAGE_MODEL}
                      onChange={(newModel) => {
                        const newProvider = getProviderForImageModel(newModel);
                        updateExperiment(slotKey, selectedId, 'model', newModel);
                        updateExperiment(slotKey, selectedId, 'provider' as any, newProvider);
                        // When switching to OpenAI for the first time, seed
                        // openaiSize from the current preset so the backend
                        // doesn't have to derive on every call. Same the
                        // other way (Gemini imageSize stays as-is).
                        const presetId = detectPreset(
                          activeExp.aspectRatio,
                          activeExp.imageSize,
                          (activeExp as any).openaiSize,
                          newProvider === 'openai',
                        );
                        const preset = ASPECT_SIZE_PRESETS.find((p) => p.id === presetId);
                        if (newProvider === 'openai' && preset) {
                          updateExperiment(slotKey, selectedId, 'openaiSize' as any, preset.openaiSize);
                        }
                      }}
                      sx={{ width: '100%' }}
                    />

                    {/* Provider-aware secondary controls — Aspect & Size
                        preset, Quality (OpenAI only), Style. */}
                    {(() => {
                      const expProvider = (activeExp.provider as 'google' | 'openai' | undefined)
                        ?? getProviderForImageModel(activeExp.model || DEFAULT_IMAGE_MODEL);
                      const isOpenAI = expProvider === 'openai';
                      const presetId = detectPreset(
                        activeExp.aspectRatio,
                        activeExp.imageSize,
                        (activeExp as any).openaiSize,
                        isOpenAI,
                      );
                      const onPresetChange = (newPresetId: string) => {
                        const preset = ASPECT_SIZE_PRESETS.find((p) => p.id === newPresetId);
                        if (!preset) return;
                        updateExperiment(slotKey, selectedId, 'aspectRatio', preset.aspectRatio);
                        // Persist BOTH provider-specific size fields so a
                        // later model swap doesn't lose the user's intent.
                        updateExperiment(slotKey, selectedId, 'imageSize', preset.imageSize);
                        updateExperiment(slotKey, selectedId, 'openaiSize' as any, preset.openaiSize);
                      };
                      return (
                        <Stack direction="row" spacing={1}>
                          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                            <InputLabel>Aspect & Size</InputLabel>
                            <Select
                              value={presetId}
                              label="Aspect & Size"
                              onChange={(e) => onPresetChange(e.target.value)}
                              disabled={disabled}
                            >
                              {ASPECT_SIZE_PRESETS.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {isOpenAI && (
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel>Quality</InputLabel>
                              <Select
                                value={(activeExp as any).quality ?? 'medium'}
                                label="Quality"
                                onChange={(e) => updateExperiment(slotKey, selectedId, 'quality' as any, e.target.value)}
                                disabled={disabled}
                              >
                                {OPENAI_QUALITY_OPTIONS.map((q) => (
                                  <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Stack>
                      );
                    })()}

                    {/* Cost warning for OpenAI 'high' quality. Pricing per
                        image at 1024×1024 confirmed via 2026-05 OpenAI docs:
                        low $0.006, medium $0.053, high $0.211. */}
                    {(activeExp as any).quality === 'high'
                      && getProviderForImageModel(activeExp.model || DEFAULT_IMAGE_MODEL) === 'openai' && (
                        <Typography variant="caption" color="warning.main">
                          High quality is ~4× the cost of medium ($0.21 vs $0.05 per image).
                        </Typography>
                      )}

                    {/* Style — cross-provider hint, free text */}
                    <TextField
                      label="Style" value={activeExp.style ?? ''}
                      onChange={(e) => updateExperiment(slotKey, selectedId, 'style', e.target.value)}
                      size="small" fullWidth placeholder="e.g., photorealistic, illustration, 3D render"
                      disabled={disabled}
                    />

                    {/* Negative Prompt */}
                    <TextField
                      label="Negative Prompt" value={activeExp.negativePrompt ?? ''}
                      onChange={(e) => updateExperiment(slotKey, selectedId, 'negativePrompt', e.target.value)}
                      size="small" fullWidth placeholder="What to exclude..." disabled={disabled}
                    />

                    {/* Reference Images */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Reference Images ({referenceImageCount}/14)
                      </Typography>
                      <Stack spacing={1}>
                        {(activeExp.referenceImages ?? []).map((ref: any, refIdx: number) => (
                          <MediaUploadField
                            key={refIdx}
                            value={ref.url || null}
                            onChange={(value: string | File | null) => handleRefChange(slotKey, selectedId, refIdx, value)}
                            mediaType="image" disabled={disabled} size="small" showPreview
                            placeholder={`Reference image ${refIdx + 1}`}
                          />
                        ))}
                        {referenceImageCount < 14 && (
                          <Button
                            size="small" variant="outlined" startIcon={<ImageIcon />} disabled={disabled}
                            onClick={() => addReference(slotKey, selectedId)}
                          >
                            Add Reference
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    {/* Generation Info */}
                    {activeExp.generationModel && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={activeExp.generationModel} size="small" variant="outlined" />
                        {formatGeneratedAt(activeExp.generatedAt) && (
                          <Chip label={formatGeneratedAt(activeExp.generatedAt)!} size="small" variant="outlined" />
                        )}
                      </Stack>
                    )}

                    {/* Error */}
                    {activeExp.status === 'error' && activeExp.error && (
                      <Typography variant="caption" color="error">{activeExp.error}</Typography>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                        variant="outlined" size="small" sx={{ flex: 1 }}
                        disabled={disabled || isGenerating || !hasPrompt || !onGenerateImage}
                        onClick={() => handleGenerate(slotKey, selectedId)}
                        data-testid={`ai-asset-generate-${slotKey}`}
                      >
                        {isGenerating ? 'Generating image...' : activeExp.status === 'completed' ? 'Regenerate image' : 'Generate image'}
                      </Button>
                      <Tooltip title="Duplicate experiment">
                        <IconButton size="small" onClick={() => duplicateExperiment(slotKey, selectedId)} disabled={disabled}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={experimentEntries.length <= 1 ? 'Cannot delete last experiment' : 'Delete experiment'}>
                        <span>
                          <IconButton
                            size="small" color="error"
                            onClick={() => deleteExperiment(slotKey, selectedId)}
                            disabled={disabled || experimentEntries.length <= 1}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>
            </Card>
          );
        })}
        <Dialog
          open={Boolean(promptRegenerationDialog)}
          onClose={isPromptRegenerationSubmitting ? undefined : handleClosePromptRegeneration}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              position: 'relative',
              px: 3,
              pt: 3,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <IconButton
              aria-label="Close regenerate prompt dialog"
              onClick={handleClosePromptRegeneration}
              disabled={isPromptRegenerationSubmitting}
              data-testid="ai-asset-prompt-regenerate-close"
              sx={{
                position: 'absolute',
                left: 16,
                top: 16,
                width: 36,
                height: 36,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ pl: 5, fontWeight: 700 }}>
              {promptRegenerationActionLabel}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 2.25 }}>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {promptRegenerationHasPrompt
                  ? 'Create a new prompt experiment from the selected prompt. The instruction is optional; generated images are preserved.'
                  : 'Create the first prompt experiment for this slot. The instruction is optional; the agent will use the screen, product, design-system, and context inputs.'}
              </Typography>
              {promptRegenerationDialog && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip label={`Slot: ${promptRegenerationDialog.slotKey}`} size="small" variant="outlined" />
                  <Chip label={`Source: ${promptRegenerationDialog.experimentId}`} size="small" variant="outlined" />
                  {promptRegenerationSourceExperiment?.status && (
                    <Chip label={`Status: ${promptRegenerationSourceExperiment.status}`} size="small" variant="outlined" />
                  )}
                </Stack>
              )}
              <TextField
                label="Instruction (optional)"
                value={promptRegenerationInstruction}
                onChange={(event) => setPromptRegenerationInstruction(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                placeholder="Example: make the visual more concrete, keep the same screen context and no readable text"
                helperText="Leave empty to let the prompt agent improve the current package using the screen and render context."
                disabled={isPromptRegenerationSubmitting}
                data-testid="ai-asset-prompt-regenerate-instruction"
              />
              <TextField
                label="Prompt concepts"
                type="number"
                value={promptRegenerationConceptCount}
                onChange={(event) => {
                  const next = readConceptCount(event.target.value);
                  if (next) {
                    setPromptRegenerationConceptCount(next);
                  }
                }}
                helperText="Creates one draft prompt experiment per returned concept. Use 3 for normal comparison."
                disabled={isPromptRegenerationSubmitting}
                fullWidth
                inputProps={{
                  min: MIN_PROMPT_REGENERATION_CONCEPT_COUNT,
                  max: MAX_PROMPT_REGENERATION_CONCEPT_COUNT,
                  step: 1,
                }}
                data-testid="ai-asset-prompt-regenerate-concept-count"
              />
              <AvailableModelSelector
                projectId={projectId}
                value={promptRegenerationModel}
                onChange={setPromptRegenerationModel}
                label="Prompt agent model"
                disabled={isPromptRegenerationSubmitting}
                sx={{ width: '100%' }}
              />
              <Box
                data-testid="ai-asset-prompt-regenerate-use-context"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1.25,
                  py: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={promptRegenerationUseContext}
                      onChange={(event) => setPromptRegenerationUseContext(event.target.checked)}
                      disabled={isPromptRegenerationSubmitting || !promptRegenerationDialog?.renderContext}
                    />
                  }
                  label="Use screen context"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.85rem',
                      fontWeight: 600,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {promptRegenerationDialog?.renderContext
                    ? `Available: ${promptRegenerationDialog.renderContext.source} (${promptRegenerationDialog.renderContext.confidence})`
                    : 'No measured screen context is available for this run.'}
                </Typography>
              </Box>
              {promptRegenerationDialog && regeneratePromptErrorBySlotKey[promptRegenerationDialog.slotKey] && (
                <Alert severity="error">
                  {regeneratePromptErrorBySlotKey[promptRegenerationDialog.slotKey]}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={handleClosePromptRegeneration}
              disabled={isPromptRegenerationSubmitting}
              variant="outlined"
              sx={{ minWidth: 184, height: 40 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isPromptRegenerationSubmitting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              onClick={handleSubmitPromptRegeneration}
              disabled={isPromptRegenerationSubmitting || !onRegeneratePrompt}
              data-testid="ai-asset-prompt-regenerate-submit"
              sx={{ minWidth: 184, height: 40 }}
            >
              {isPromptRegenerationSubmitting ? 'Starting...' : promptRegenerationActionLabel}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SAccordion>
  );
};
